import { useEffect, useMemo, useState } from 'react';
import {
  fetchActiveServiceProviderLocations,
  getDefaultAcceptLocationOption,
} from '@product-framework/utils/acceptJobLocation';

/**
 * Loads active service provider locations when the dialog is open and derives
 * UI flags shared by Accept Job and Conditionally Accept Job dialogs.
 */
export default function usePendingAcceptanceLocations({
  open,
  serviceProviderId,
  get,
  getApiUrl,
  jobId,
  dispatchEvent,
}) {
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [activeLocations, setActiveLocations] = useState([]);

  useEffect(() => {
    if (!open || !serviceProviderId) {
      setActiveLocations([]);
      setLocationsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLocationsLoading(true);
    fetchActiveServiceProviderLocations(get, getApiUrl, serviceProviderId).then(
      list => {
        if (!cancelled) {
          setActiveLocations(Array.isArray(list) ? list : []);
          setLocationsLoading(false);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, serviceProviderId, jobId, get, getApiUrl]);

  const activeCount = activeLocations.length;
  const singleLocationId = activeCount === 1 ? activeLocations[0].id : null;
  const showLocationField = activeCount > 1;

  const defaultLocationOption = useMemo(() => {
    if (!showLocationField) {
      return null;
    }
    return getDefaultAcceptLocationOption(activeLocations, dispatchEvent);
  }, [showLocationField, activeLocations, dispatchEvent]);

  const showNoDefaultLocationMessage =
    showLocationField && defaultLocationOption == null;
  const hasNoActiveLocations = !locationsLoading && activeCount === 0;

  const formMountKey = useMemo(
    () =>
      `${jobId ?? ''}-${activeCount}-${defaultLocationOption?.value ?? 'x'}`,
    [jobId, activeCount, defaultLocationOption?.value],
  );

  return {
    locationsLoading,
    activeCount,
    singleLocationId,
    showLocationField,
    defaultLocationOption,
    showNoDefaultLocationMessage,
    hasNoActiveLocations,
    formMountKey,
  };
}
